import React, { useContext, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  TextField,
} from '@mui/material';
import { MAutocomplete, MDropdown } from 'common';
import * as R from 'ramda';
import { useUserRolePermissions } from 'hooks';
import { isNotEmpty } from 'utils/helpers/formValidations';
import { REQUIRED } from 'utils/helpers/validationMessages';
import {
  useCreateControlClassMutation,
  useGetCitiesAndZipsQuery,
  useGetClassesQuery,
  useGetStatesQuery,
} from 'feature/loading-management/customer-authorizations/customerAuthorizationsEndpoints';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import { useDispatch } from 'react-redux';
import { createSnack } from 'snackbar/snackbarSlice';
import {
  CreateCustomerAuthContext,
  initialStateStep2,
  initialStateStep3,
  initialStateStep4,
} from '../Create';

const resetState = R.mergeAll([initialStateStep4, initialStateStep3, initialStateStep2]);
const cardStyles = {
  /* eslint-disable sort-keys-fix/sort-keys-fix */
  m: theme => ({ xs: theme.spacing(0, 'auto', 10.5), lg: theme.spacing(3, 'auto', 12) }),
  p: { xs: 2, lg: 3 },
  pb: { xs: 0, lg: 3 },
  width: { xs: 1, lg: 760 },
};
const CustomerForm = () => {
  const { currentAffiliate } = useUserRolePermissions();
  const { info, setInfo } = useContext(CreateCustomerAuthContext);
  const {
    address,
    address2,
    city,
    // eslint-disable-next-line no-unused-vars
    customerClass = { customerClassId: '', customerClassName: '' },
    customerIdName,
    customerName,
    phoneNumber,
    state,
    zipCode,
  } = info;
  const [inputValue, setInputValue] = useState('');
  const [showCreateButton, setShowCreateButton] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [inputError, setInputError] = useState(false);
  const dispatch = useDispatch();

  const params = {
    supplier: {
      companyCustomerIdName: currentAffiliate?.customerIdName,
      dataCompanyId: currentAffiliate?.dataCompanyId,
    },
  };

  // setInfoAndReset :: info -> info
  const setInfoAndReset = R.o(setInfo, R.mergeLeft(resetState));

  const { data: allStates, isLoading: isLoadingStates } = useGetStatesQuery();
  const states = allStates ? R.map(({ id, name }) => ({ label: name, value: id }), allStates) : [];
  const skipClassFetch = R.isNil(currentAffiliate);
  const {
    data: customerClasses = [],
    isLoading: isLoadingCustomerClasses,
    refetch: refetchClasses,
  } = useGetClassesQuery({ params }, { skip: skipClassFetch });

  // eslint-disable-next-line no-unused-vars
  const [submit, { isLoading: isCreatingControlClass }] = useCreateControlClassMutation();

  const handleCreateNewClass = async () => {
    await submit({
      body: {
        supplier: {
          companyCustomerIdName: R.prop('customerIdName', currentAffiliate),
          dataCompanyId: R.prop('dataCompanyId', currentAffiliate),
        },
        controlClassName: inputValue,
        isLoadingAllowed: true,
      },
    })
      .then(res => {
        if (res.error) {
          const errorMessage = `Error: Please try again`;
          dispatch(
            createSnack({
              message: errorMessage,
              options: { variant: 'error' },
            })
          );
        } else {
          const successMessage = `Success: Class created successfully!`;
          dispatch(
            createSnack({
              message: successMessage,
            })
          );
        }
      })
      .finally(() => {
        setInputValue('');
        setShowCreateButton(false);
        setIsTyping(false);
        refetchClasses();
      });
  };

  const skipCityZipFetch = R.or(R.isNil(state), R.isEmpty(state));
  const foundState = states.find(found => found.value === state);
  const stateParmas = { state: foundState?.label };
  const { data: citiesZips, isLoading: isLoadingCityZip } = useGetCitiesAndZipsQuery(
    { params: stateParmas },
    {
      skip: skipCityZipFetch,
    }
  );

  const cities = R.not(R.isNil(R.prop('citiesAndZips', citiesZips)))
    ? R.map(item => ({ label: item.city, value: item.city }), citiesZips.citiesAndZips)
    : [];

  const zips =
    R.not(R.isNil(city)) && R.not(R.isNil(R.prop('citiesAndZips', citiesZips)))
      ? R.map(
          zip => ({ label: zip, value: zip }),
          citiesZips.citiesAndZips.find(foundCity => foundCity.city === city).zips
        )
      : [];

  const [validation, setValidation] = useState({
    customerName: '',
  });

  const isDisabled = R.or(R.isNil(customerIdName), R.isEmpty(customerIdName));
  const defaultToEmpty = R.defaultTo('');

  const validateRequired = R.cond([
    [R.isEmpty, R.always(REQUIRED)],
    [R.T, R.always('')],
  ]);
  const [customerNameExceedsLimit, setCustomerNameExceedsLimit] = useState(false);
  const handleReqFormChange = e => {
    const value = R.path(['target', 'value'], e);
    const name = R.path(['target', 'name'], e);
    if (value.length > 40) {
      setCustomerNameExceedsLimit(true);
    } else {
      setCustomerNameExceedsLimit(false);
      setInfo(R.mergeLeft(resetState, R.assoc(name, value, info)));
    }
    const errorMessage = validateRequired(value);
    setValidation(R.assoc(name, errorMessage, validation));
  };

  const handleNonReqFormChange = e =>
    setInfo(
      R.mergeLeft(
        resetState,
        R.assoc(R.path(['target', 'name'], e), R.path(['target', 'value'], e), info)
      )
    );

  const handleStateChange = val => {
    setInfo(
      R.evolve(
        {
          city: R.always(undefined),
          state: R.always(val),
          zipCode: R.always(undefined),
        },
        R.mergeLeft(resetState, info)
      )
    );
  };

  const handleCityChange = val => {
    setInfo(
      R.evolve({
        city: R.always(val),
        zipCode: R.always(undefined),
      }),
      R.mergeLeft(resetState, info)
    );
  };

  const containsSpecialCharacters = str => {
    const specialChars = /[`!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?~]/;
    return specialChars.test(str);
  };

  const handleInputChange = (evert, newInputValue) => {
    setInputValue(newInputValue);
    const isNewInput =
      newInputValue !== '' &&
      !containsSpecialCharacters(newInputValue) &&
      !customerClasses.some(classObj => classObj.customerClassName === newInputValue);
    setShowCreateButton(isNewInput);
    setIsTyping(isNewInput);

    if (containsSpecialCharacters(newInputValue)) {
      setInputError(true);
    } else {
      setInputError(false);
    }
  };

  const renderInput = p => {
    return (
      <TextField
        {...p}
        error={inputError}
        helperText={inputError ? 'special characters are not allowed' : ''}
        label="Class"
      />
    );
  };

  return (
    <Card sx={cardStyles} variant="create">
      <CardContent variant="create">
        <TextField
          disabled={isDisabled}
          error={isNotEmpty(validation.customerName) || customerNameExceedsLimit}
          helperText={customerNameExceedsLimit ? '40 Characters Max' : validation.customerName}
          label="Customer Name"
          name="customerName"
          onBlur={e => {
            setCustomerNameExceedsLimit(false);
            setInfo(R.mergeLeft(resetState, R.assoc('customerName', e.target.value, info)));
            const errorMessage = validateRequired(customerName);
            setValidation(R.assoc('customerName', errorMessage, validation));
          }}
          onChange={handleReqFormChange}
          required
          value={defaultToEmpty(customerName)}
          variant="outlined"
        />
        <TextField
          disabled={isDisabled}
          label="Street Address"
          name="address"
          onChange={handleNonReqFormChange}
          value={defaultToEmpty(address)}
          variant="outlined"
        />
        <TextField
          disabled={isDisabled}
          helperText="(Optional)"
          label="Suite, Apt. #, PO Box"
          name="address2"
          onChange={handleNonReqFormChange}
          value={defaultToEmpty(address2)}
          variant="outlined"
        />
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', lg: 'row' },
            gridColumnGap: { xs: 0, lg: 16 },
            gridRowGap: { xs: 16, lg: 0 },
          }}
        >
          <Box sx={{ flex: { xs: 1, lg: 3 } }}>
            <MDropdown
              disabled={isDisabled}
              id="state-select"
              items={states}
              label="State"
              loading={isLoadingStates}
              name="state"
              onChange={({ value }) => handleStateChange(value)}
              value={defaultToEmpty(state)}
            />
          </Box>
          <MAutocomplete
            disabled={R.isNil(state)}
            fullWidth
            helperText={R.isNil(state) ? 'Please select state first' : ''}
            id="city"
            label="City"
            loading={isLoadingCityZip}
            name="city"
            onChange={(_, obj, reason) => {
              reason === 'clear'
                ? setInfoAndReset(R.assoc('city', undefined, info))
                : handleCityChange(obj.value);
            }}
            options={cities}
            sx={{
              // eslint-disable-next-line sort-keys-fix/sort-keys-fix
              flex: { xs: 1, lg: 4 },
            }}
            value={defaultToEmpty(city)}
          />
          <MAutocomplete
            disabled={R.isNil(city)}
            fullWidth
            helperText={R.isNil(city) ? 'Please select state & city first' : ''}
            id="zipCode"
            label="Zip Code"
            loading={isLoadingCityZip}
            name="zipCode"
            onChange={(_, obj, reason) => {
              reason === 'clear'
                ? setInfoAndReset(R.assoc('zipCode', undefined, info))
                : setInfoAndReset(R.assoc('zipCode', obj.value, info));
            }}
            options={zips}
            sx={{
              // eslint-disable-next-line sort-keys-fix/sort-keys-fix
              flex: { xs: 1, lg: 3 },
            }}
            value={defaultToEmpty(zipCode)}
          />
        </Box>
        <TextField
          disabled={isDisabled}
          helperText="(Optional)"
          label="Phone Number"
          name="phoneNumber"
          onChange={handleNonReqFormChange}
          value={defaultToEmpty(phoneNumber)}
          variant="outlined"
        />
        <Autocomplete
          disabled={isDisabled}
          freeSolo
          fullWidth
          getOptionLabel={R.prop('customerClassName')}
          isOptionEqualToValue={R.eqProps('customerClassId')}
          loading={isLoadingCustomerClasses}
          onChange={(_, obj, reason) => {
            reason === 'clear'
              ? setInfoAndReset(R.assoc('customerClass', undefined, info))
              : setInfoAndReset(R.assoc('customerClass', obj, info));
          }}
          onInputChange={handleInputChange}
          options={customerClasses}
          renderInput={renderInput}
        />
        <>
          {isTyping && (
            <div
              style={{
                display: 'flex',
                justifyContent: 'right',
                marginTop: '7px',
              }}
            >
              <CircularProgress />
            </div>
          )}
          {showCreateButton && (
            <Box sx={{ alignItems: 'center', display: 'flex', justifyContent: 'right' }}>
              <Button
                onClick={() => {
                  handleCreateNewClass();
                }}
                style={{ marginTop: '5px', marginRight: '5px', borderRadius: '-4px' }}
                variant="contained"
              >
                Create New Class <AddRoundedIcon color="inherit" sx={{ mt: '-2px' }} />
              </Button>
            </Box>
          )}
        </>
      </CardContent>
    </Card>
  );
};

export default CustomerForm;
